import { Fragment, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { useBrand } from '../context/BrandContext';
const API_URL = process.env.REACT_APP_API_URL || '';
const isValidPAN = p => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(p);
const stripHtml = text => String(text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const friendlyErrorMessage = (status, rawMessage) => {
  const normalized = stripHtml(rawMessage);
  const lower = normalized.toLowerCase();

  if (!normalized) {
    return status >= 500
      ? 'Our server is temporarily unavailable. Please try again in a moment.'
      : `Request failed with status ${status}`;
  }

  if (
    lower.includes('bad gateway') ||
    lower.includes('gateway timeout') ||
    lower.includes('service unavailable') ||
    lower.includes('nginx')
  ) {
    return 'Server is temporarily unavailable. Please try again shortly.';
  }

  return normalized;
};

/* ── Illustration Carousel ────────────────── */
function IllustrationCarousel() {
  const images = [
    '/images/payday_ref_style_1_1788578367934.jpg',
    '/images/payday_ref_style_2_1788578379191.jpg',
    '/images/payday_ref_style_3_1788578391246.jpg'
  ];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="lp-carousel" style={{ position: 'relative', width: '100%', height: '100%', minHeight: '500px', overflow: 'hidden', borderRadius: '24px' }}>
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`Illustration ${i + 1}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: current === i ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        />
      ))}
      <div style={{
        position: 'absolute',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px',
        zIndex: 10
      }}>
        {images.map((_, i) => (
          <div
            key={i}
            onClick={() => setCurrent(i)}
            style={{
              width: current === i ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: current === i ? 'var(--gold, #d4af37)' : 'rgba(255,255,255,0.5)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Brand header — logo with a graceful text fallback if the image 404s ──
   Uses the icon-only mark (not the shared full lockup) since this spot is a
   compact inline logo+wordmark pairing, not a stacked lockup. ── */
function BrandHeader() {
  const { logoAlt: LOGO_ALT, brandName } = useBrand();
  const [broken, setBroken] = useState(false);

  return (
    <div className="lp-brand-header">
      {!broken ? (
        <img src="/finmudra-icon.png" alt={LOGO_ALT} className="lp-brand-logo" onError={() => setBroken(true)} />
      ) : (
        <span className="lp-brand-fallback">{brandName?.[0] || 'F'}</span>
      )}
      <span className="lp-brand-name">{brandName}</span>
    </div>
  );
}

/* ── Crisp validation check ────────────────────────────── */
function CheckIcon() {
  return (
    <span className="lp-check">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

/* ── Trust strip (reassurance for a lending login) ─────── */
function TrustStrip() {
  const items = [
    {
      label: '256-bit encrypted',
      icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    },
    {
      label: 'RBI-aligned',
      icon: <><path d="M9 12l2 2 4-4" /><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    },
    {
      label: 'No spam calls',
      icon: <><path d="M18.5 3.5 3.5 18.5" /><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72" /></>,
    },
  ];
  return (
    <div className="lp-trust">
      {items.map((it, i) => (
        <span className="lp-trust-item" key={i}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {it.icon}
          </svg>
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── Alert ─────────────────────────────────────────────── */
function Alert({ type, msg }) {
  if (!msg) return null;
  return (
    <div className={`lp-alert lp-alert-${type}`}>
      {type === 'error'
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>}
      {msg}
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MAIN PAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export default function LoginPage() {
  const navigate = useNavigate();
  const panInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const { brandName } = useBrand();

  /* shared */
  const [pan, setPan] = useState('');
  const [phone, setPhone] = useState('');
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* OTP */
  const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
  const [showOtp, setShowOtp] = useState(false);
  const [resendT, setResendT] = useState(0);
  const [expiryT, setExpiryT] = useState(0);
  const [canResend, setCanResend] = useState(false);

  /* Credit executive affiliate link — captured once on load so a new
     applicant gets auto-assigned instead of landing unassigned in the queue. */
  const [referralCode] = useState(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('ref');
      if (fromUrl) {
        sessionStorage.setItem('referralCode', fromUrl);
        return fromUrl;
      }
      return sessionStorage.getItem('referralCode') || '';
    } catch {
      return '';
    }
  });

  const panStatus = pan.length === 0 ? 'empty' : isValidPAN(pan) ? 'valid' : pan.length < 10 ? 'typing' : 'invalid';

  useEffect(() => { setError(''); setSuccess(''); }, [pan, phone]);

  /* countdown timers */
  useEffect(() => {
    if (resendT <= 0) { if (showOtp) setCanResend(true); return; }
    const t = setTimeout(() => setResendT(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendT, showOtp]);

  useEffect(() => {
    if (!showOtp || expiryT <= 0) return;
    const t = setTimeout(() => setExpiryT(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [expiryT, showOtp]);

  useEffect(() => {
    if (showOtp && expiryT === 0) {
      setError('OTP expired. Please request a new one.');
      setOtpDigits(['', '', '', '']);
      setResendT(0);
      setCanResend(true);
    }
  }, [expiryT, showOtp]);

  /* helpers */
  const post = async (path, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const raw = await res.text();
    let data = {};

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {
        success: false,
        message: friendlyErrorMessage(res.status, raw),
      };
    }

    if (!res.ok && data?.message) {
      data.message = friendlyErrorMessage(res.status, data.message);
    }

    return { ok: res.ok, status: res.status, data };
  };

  const saveSession = (user, token) => {
    localStorage.setItem('userSession', JSON.stringify({
      userId: user.id, phone: user.phone, pan: user.pan_number,
      name: user.name, role: user.role, loginTime: Date.now(),
      expiresAt: Date.now() + 30 * 60 * 1000,
      clientId: user.client_id,
      token: token
    }));
    navigate('/dashboard', { replace: true });
  };

  const startOtp = (expiresInSeconds = 600) => {
    setShowOtp(true); setResendT(60);
    setExpiryT(Number(expiresInSeconds) || 600);
    setCanResend(false); setOtpDigits(['', '', '', '']);
  };

  const handleOtpKey = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const n = [...otpDigits]; n[i] = val; setOtpDigits(n);
    if (val && i < 3) document.getElementById(`otp-${i + 1}`)?.focus();
  };

  /* ── Unified: Request OTP ── */
  const handleRequestOtp = async e => {
    if (e?.preventDefault) e.preventDefault();
    if (!isValidPAN(pan)) return setError('Enter a valid PAN (e.g. ABCDE1234F)');
    if (phone.length !== 10) return setError('Enter a valid 10-digit mobile number');
    if (!terms) return setError('Please accept Terms, Privacy Policy and Bureau Consent');
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { ok, data } = await post('/api/auth/request-otp', { panNumber: pan, phone, consentAccepted: terms, debug: true });
      if (ok && data.success) { startOtp(data.expiresIn); setSuccess('OTP sent!'); setTimeout(() => setSuccess(''), 3000); }
      else setError(data.message || 'Failed to send OTP');
    } catch { setError('Unable to reach the server. Please try again.'); }
    finally { setLoading(false); }
  };

  /* ── Unified: Verify OTP ── */
  const handleVerifyOtp = async e => {
    e.preventDefault();
    const code = otpDigits.join('');
    if (code.length !== 4) return;
    setLoading(true);
    try {
      const { ok, data } = await post('/api/auth/verify-otp', { panNumber: pan, phone, otp: code, consentAccepted: terms, debug: true, referralCode });
      if (ok && data.success) {
        if (data.message) setSuccess(data.message);
        if (data.isNewUser) {
          try { sessionStorage.removeItem('referralCode'); } catch { /* ignore */ }
          setTimeout(() => saveSession(data.user, data.token), 1200);
        } else {
          saveSession(data.user, data.token);
        }
      } else {
        setError(data.message || 'Invalid OTP');
        if (data.message && data.message.toLowerCase().includes('incorrect pan')) {
          setTimeout(() => setError(''), 2000);
        }
      }
    } catch { setError('Unable to reach the server. Please try again.'); }
    finally { setLoading(false); }
  };

  const isFormReady = isValidPAN(pan) && phone.length === 10 && terms;

  /* ---- render helpers (plain functions, NOT components — avoids remount on re-render) ---- */
  const renderPanField = () => (
    <div className={`lp-field ${['typing', 'invalid'].includes(panStatus) ? panStatus : ''}`}>
      <label>PAN Number</label>
      <div className="lp-input-wrap" onMouseDown={e => e.target.tagName !== 'INPUT' && panInputRef.current?.focus()}>
        <span className="lp-input-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
        </span>
        <input ref={panInputRef} id="pan-f" type="text" placeholder="ABCDE1234F" value={pan} maxLength={10}
          onChange={e => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          autoComplete="off" spellCheck={false}
          style={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }} />
        {panStatus === 'valid' && <CheckIcon />}
      </div>
      {pan.length > 0 && pan.length < 10 && <span className="lp-hint">{10 - pan.length} more chars</span>}
      {panStatus === 'invalid' && <span className="lp-hint err">Invalid PAN format</span>}
    </div>
  );

  const renderPhoneField = () => (
    <div className="lp-field">
      <label>Mobile Number</label>
      <div className="lp-input-wrap" onMouseDown={e => e.target.tagName !== 'INPUT' && phoneInputRef.current?.focus()}>
        <span className="lp-input-prefix" style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
          +91
        </span>
        <input ref={phoneInputRef} id="ph-f" type="tel" placeholder="Enter 10-digit number" value={phone} maxLength={10}
          onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
        {phone.length === 10 && <CheckIcon />}
      </div>
    </div>
  );

  const renderTermsRow = () => (
    <div className="lp-check-row">
      <label className="lp-checkbox">
        <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} />
        <span className="lp-check-box" />
        <span className="lp-checkbox-text">
          I agree to the <Link to="/privacy">Terms</Link>, <Link to="/privacy">Privacy Policy</Link> and Bureau Consent for credit report pull.
        </span>
      </label>
    </div>
  );

  const renderBtnPrimary = (label, disabled, showArrow = true) => (
    <button type="submit" className={`lp-btn-primary ${loading ? 'is-loading' : ''}`} disabled={disabled || loading}>
      {loading
        ? <span className="lp-spinner" />
        : <>
          {disabled && <svg className="lp-btn-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>}
          {label}
        </>}
      {!loading && !disabled && showArrow && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>}
    </button>
  );

  const renderOtpBoxes = (onSubmit) => (
    <form className="lp-form" onSubmit={onSubmit}>
      <div className="lp-field">
        <label>Enter 4-digit OTP</label>
        <div className="lp-otp-row">
          {otpDigits.map((d, i) => (
            <input key={i} id={`otp-${i}`} className="lp-otp-box"
              type="text" inputMode="numeric" maxLength={1} value={d}
              onChange={e => handleOtpKey(i, e.target.value)}
              onKeyDown={e => e.key === 'Backspace' && !d && i > 0 && document.getElementById(`otp-${i - 1}`)?.focus()}
              autoFocus={i === 0} />
          ))}
        </div>
        <div className="lp-otp-meta">
          {resendT > 0
            ? <span className="lp-resend-wait">Resend in {resendT}s</span>
            : <button type="button" className="lp-resend-btn" onClick={handleRequestOtp} disabled={!canResend || loading}>Resend OTP</button>}
        </div>
      </div>
      {renderBtnPrimary('Verify & Continue', otpDigits.join('').length !== 4)}
      <a href="/login" className="lp-login-link" onClick={e => { e.preventDefault(); setShowOtp(false); setOtpDigits(['', '', '', '']); setError(''); setSuccess(''); navigate('/login'); }}>
        ← Login
      </a>
    </form>
  );

  /* ━━━━━━━━━━━━━━━━━━━━━━ RENDER ━━━━━━━━━━━━━━━━━━━━━━ */
  return (
    <div className="lp-root">
      <div className="lp-shell">

        {/* ── LEFT ── */}
        <div className="lp-left">
          <IllustrationCarousel />
        </div>

        {/* ── RIGHT ── */}
        <div className="lp-right">
          <div className="lp-right-inner">
            <BrandHeader />

            <div className="lp-card">
              <div className="lp-card-head">
                <span className="lp-card-eyebrow">{showOtp ? 'One last step' : 'Welcome back'}</span>
                <h2>
                  {showOtp
                    ? 'Verify your number'
                    : <>Welcome to <span className="lp-brand-accent">{brandName}</span></>}
                </h2>
                <p className="lp-card-sub">
                  {showOtp
                    ? <>We sent a 4-digit code to <strong>+91&nbsp;{phone.slice(0, 2)}••••••{phone.slice(-2)}</strong></>
                    : 'Enter your PAN & mobile to sign in or create your account.'}
                </p>
              </div>

              <Alert type="error" msg={error} />
              <Alert type="success" msg={success} />

              {/* ── UNIFIED FLOW ── */}
              {!showOtp ? (
                <form className="lp-form" onSubmit={handleRequestOtp}>
                  {renderPanField()}
                  {renderPhoneField()}
                  {renderTermsRow()}
                  {renderBtnPrimary('Get OTP', !isFormReady, false)}
                  <TrustStrip />
                </form>
              ) : renderOtpBoxes(handleVerifyOtp)}

              <p className="lp-card-footer" style={{ marginTop: '2rem' }}>
                Secured by Signzy PAN Verification
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
